import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Globe, Plus, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { StatsCards } from "@/components/admin/StatsCards";
import { Filters } from "@/components/admin/Filters";
import { AppointmentsTable } from "@/components/admin/AppointmentsTable";
import { WalkInDialog } from "@/components/admin/WalkInDialog";
import { ChangePasswordDialog } from "@/components/admin/ChangePasswordDialog";
import { DeleteAppointmentDialog } from "@/components/admin/DeleteAppointmentDialog";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ status: "all", date: "", q: "" });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    const params = { ...(filters.status !== "all" && { status: filters.status }), ...(filters.date && { date: filters.date }), ...(filters.q && { q: filters.q }) };
    try {
      const [s, a] = await Promise.all([api.get("/admin/stats"), api.get("/admin/appointments", { params })]);
      setStats(s.data);
      setItems(a.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [filters]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      toast.success(`Appointment marked ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-[#DC2626] text-white grid place-items-center"><Plus className="h-5 w-5" strokeWidth={3} /></span>
            <div className="leading-tight">
              <p className="font-display font-bold text-[#1A252C]">Clinic dashboard</p>
              <p className="text-xs text-muted-foreground" data-testid="admin-user-email">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="ghost" data-testid="admin-poster-btn"><Link to="/admin/poster"><QrCode className="mr-1 h-4 w-4" /><span className="hidden sm:inline">QR poster</span></Link></Button>
            <Button asChild variant="ghost" data-testid="admin-view-site-btn"><Link to="/"><Globe className="mr-1 h-4 w-4" /><span className="hidden sm:inline">Website</span></Link></Button>
            <ChangePasswordDialog />
            <Button variant="outline" onClick={logout} data-testid="admin-logout-btn"><LogOut className="mr-1 h-4 w-4" /><span className="hidden sm:inline">Log out</span></Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A252C]">Appointments</h1>
            <p className="mt-1 text-muted-foreground">Confirm requests and manage today's schedule.</p>
          </div>
          <WalkInDialog onCreated={load} />
        </div>
        <StatsCards stats={stats} />
        <div className="bg-white rounded-3xl border border-slate-100 p-4 sm:p-6 space-y-6">
          <Filters filters={filters} setFilters={setFilters} todayDate={stats?.today_date} />
          <AppointmentsTable items={items} onStatus={updateStatus} onDelete={setToDelete} />
          <DeleteAppointmentDialog appt={toDelete} onClose={() => setToDelete(null)} onDeleted={load} />
        </div>
      </main>
    </div>
  );
}
